<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { navItems, seo } from '~/data/pages/banks/mocks'
import type { IResponseData } from '~/types'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetVacanciesList } from '~/components/widget/vacancies/list'
import { WidgetBankServicesMenu } from '~/components/widget/bank'
import type { IMicroCredit } from '~/components/entity/microcredit/item'
import { WidgetAboutPageHeader } from '~/components/widget/about-page'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { useSeoFromCustom } from '~/composables/useSeoFromCustom'
import { WidgetSeoContent } from '~/components/widget/seo'

useSeoMeta(seo)
const route = useRoute()
const { t, locale } = useI18n()
const url = `/microcredit/${route.params.microcredit}`
const { data } = await useApiWith404<IResponseData<IMicroCredit>>(() => useUrlWithParams(url), {
  key: `microcredit-vacancies-${route.params.microcredit}`
})
const microcredit = computed(() => data.value?.data)

const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin

const seoData = computed(() => ({
  organization: {
    title: microcredit.value?.title
  }
}))

useSeoFromCustom(seoData, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.microcredits'), url: '/microcredit' },
  {
    title: microcredit.value?.title || t('common.microcredits'),
    url: `/microcredit/${route.params.microcredit}`
  },
  { title: t('common.vacancies') }
])
const fitOrganization = computed(() => {
  return {
    type: microcredit.value?.type || '',
    title: microcredit.value?.title || '',
    logo: microcredit.value?.logo,
    url: microcredit.value?.url || '',
    description: microcredit.value?.description || '',
    head_branch_location: microcredit.value?.head_branch_location || '',
    call_center_phone: microcredit.value?.call_center_phone || '',
    organization: {
      official_title: microcredit.value?.official_title || '',
      url: microcredit.value?.url || ''
    },
    views_count: microcredit.value?.views_count || 0,
    updated_at: microcredit.value?.updated_at || ''
  }
})
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts v-if="microcredit" :organization="microcredit" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div class="space-y-5">
          <WidgetAboutPageHeader :type="'microcredit'" :product="fitOrganization">
            <WidgetBankServicesMenu
              v-if="microcredit && microcredit.status === 'active'"
              :organization="microcredit"
              :nav-items="navItems"
              :type="'microcredit'"
            />
          </WidgetAboutPageHeader>
          <WidgetSeoContent
            heading-level="h3"
            leading-level="8"
            position="upper"
            :variables="{
              organization: microcredit?.official_title ?? ''
            }"
          />
          <WidgetVacanciesList
            v-if="microcredit && microcredit.id"
            :hide-organization-details="true"
            :query-params="{
              per_page: '20',
              'filter[organization_id]': microcredit.id.toString()
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
